import React, { useEffect, useState } from "react";
import { api, useAuth } from "../lib/api";
import { PageHeader } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash, ShieldCheck, EnvelopeSimple, UserCircle, Key, CircleNotch, BellRinging, Database } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TeamAccess() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [allowlist, setAllowlist] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "hr" });

  // Change password state
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState("");

  const load = async () => {
    const [u, a] = await Promise.all([api.get("/team/users"), api.get("/team/allowlist")]);
    setUsers(u.data); setAllowlist(a.data);
  };
  useEffect(() => { load(); }, []);

  const changePassword = async () => {
    setPwError("");
    if (pwForm.new_password.length < 8) { setPwError("New password must be at least 8 characters"); return; }
    if (pwForm.new_password !== pwForm.confirm) { setPwError("New passwords do not match"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Password updated");
      setPwOpen(false);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      setPwError(e.response?.data?.detail || "Failed to change password");
    } finally { setPwBusy(false); }
  };

  const invite = async () => {
    if (!form.email) { toast.error("Email required"); return; }
    try {
      await api.post("/team/allowlist", form);
      toast.success(`${form.email} invited`); setOpen(false); setForm({ email: "", role: "hr" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Invite failed"); }
  };

  const removeInvite = async (email) => {
    if (!window.confirm(`Revoke invite for ${email}?`)) return;
    await api.delete(`/team/allowlist/${encodeURIComponent(email)}`); toast.success("Revoked"); load();
  };

  const changeRole = async (uid, role) => {
    try { await api.put(`/team/users/${uid}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const revoke = async (u) => {
    if (!window.confirm(`Revoke access for ${u.email}? They will not be able to sign in again.`)) return;
    try { await api.delete(`/team/users/${u.user_id}`); toast.success("Revoked"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const [remBusy, setRemBusy] = useState(false);
  const runReminders = async () => {
    if (!window.confirm("Send reminder emails now?\n\n• Candidates with 5+ day unopened onboarding links\n• Contractors who haven't logged hours in 7+ days\n\nReminders run automatically every day at 09:00 UTC — this is only for on-demand runs.")) return;
    setRemBusy(true);
    try {
      const r = await api.post("/admin/run-reminders");
      const o = r.data.onboarding || {}; const t = r.data.timesheet || {};
      toast.success(`Reminders sent — Onboarding: ${o.sent}, Timesheet: ${t.sent}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setRemBusy(false); }
  };

  const [demoBusy, setDemoBusy] = useState(false);
  const loadDemoData = async () => {
    if (!window.confirm("Load demo data?\n\nThis will APPEND 4 realistic clients, 5 projects, and 1 proposal to your existing data — nothing is deleted.\n\nUseful for exploring the app. You can delete these entries anytime.")) return;
    setDemoBusy(true);
    try {
      const r = await api.post("/seed/demo");
      const c = r.data.counts || {};
      toast.success(`Demo data loaded — ${c.clients} clients, ${c.projects} projects, ${c.proposals} proposal`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setDemoBusy(false); }
  };

  return (
    <div>
      <PageHeader
        overline="Access Control"
        title="Team Access"
        subtitle="Only invited emails can create an account. Manage active users and pending invites here."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="load-demo-btn" onClick={loadDemoData} disabled={demoBusy} variant="outline" className="border-slate-300 bg-white hover:bg-slate-50 text-[#0F1E38] font-mono uppercase tracking-wider text-xs">
              {demoBusy ? <CircleNotch size={14} className="animate-spin mr-2" /> : <Database size={14} className="mr-2" weight="duotone" />} Load Demo Data
            </Button>
            <Button data-testid="run-reminders-btn" onClick={runReminders} disabled={remBusy} variant="outline" className="border-slate-300 bg-white hover:bg-slate-50 text-[#0F1E38] font-mono uppercase tracking-wider text-xs">
              {remBusy ? <CircleNotch size={14} className="animate-spin mr-2" /> : <BellRinging size={14} className="mr-2" weight="duotone" />} Run Reminders
            </Button>
            <Dialog open={pwOpen} onOpenChange={setPwOpen}>
              <DialogTrigger asChild>
                <Button data-testid="change-password-btn" variant="outline" className="border-slate-300 bg-white hover:bg-slate-50 text-[#0F1E38] font-mono uppercase tracking-wider text-xs"><Key size={14} className="mr-2"/> Change Password</Button>
              </DialogTrigger>
              <DialogContent className="bg-white text-[#0F1E38] max-w-md">
                <DialogHeader><DialogTitle>Change Your Password</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Current password</Label><Input data-testid="cp-current" type="password" value={pwForm.current_password} onChange={(e) => setPwForm({...pwForm, current_password: e.target.value})} className="mt-1"/></div>
                  <div><Label>New password</Label><Input data-testid="cp-new" type="password" value={pwForm.new_password} onChange={(e) => setPwForm({...pwForm, new_password: e.target.value})} className="mt-1" minLength={8}/><div className="text-[10.5px] text-slate-500 mt-1 font-mono">Minimum 8 characters</div></div>
                  <div><Label>Confirm new password</Label><Input data-testid="cp-confirm" type="password" value={pwForm.confirm} onChange={(e) => setPwForm({...pwForm, confirm: e.target.value})} className="mt-1" minLength={8}/></div>
                  {pwError && <div data-testid="cp-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{pwError}</div>}
                </div>
                <DialogFooter><Button data-testid="cp-submit" onClick={changePassword} disabled={pwBusy} className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold">{pwBusy ? <CircleNotch size={14} className="animate-spin" /> : "Update Password"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="invite-user-btn" className="bg-[#0F1E38] hover:bg-[#1E3A5F] text-white font-mono uppercase tracking-wider text-xs"><Plus size={14} className="mr-2"/> Invite User</Button>
              </DialogTrigger>
              <DialogContent className="bg-white text-[#0F1E38] max-w-md">
                <DialogHeader><DialogTitle>Invite New User</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Email</Label><Input data-testid="invite-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1" placeholder="teammate@company.com"/></div>
                  <div>
                    <Label>Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                      <SelectTrigger data-testid="invite-role" className="mt-1"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="hr">HR</SelectItem>
                        <SelectItem value="sales">Sales</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="text-[11px] text-slate-500 pt-2">The invitee can create an account by signing in with this email at your app URL.</div>
                </div>
                <DialogFooter><Button data-testid="send-invite-btn" onClick={invite} className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold">Send Invite</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {/* Active users */}
      <div className="border border-slate-200 bg-white rounded-lg overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <UserCircle size={16} className="text-[#0F1E38]" />
          <div className="overline">Active Users ({users.length})</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left overline border-b border-slate-100">
              <th className="px-5 py-2.5">User</th><th>Role</th><th>Joined</th><th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-b border-slate-100">
                <td className="px-5 py-3">
                  <div className="text-[#0F1E38] font-medium">{u.name || u.email}</div>
                  <div className="text-xs text-slate-500 font-mono">{u.email}</div>
                </td>
                <td>
                  {u.user_id === me?.user_id ? (
                    <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700">{u.role} · you</span>
                  ) : (
                    <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                      <SelectTrigger data-testid={`role-select-${u.user_id}`} className="h-7 text-[10px] font-mono uppercase w-28"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">admin</SelectItem>
                        <SelectItem value="hr">hr</SelectItem>
                        <SelectItem value="sales">sales</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </td>
                <td className="font-mono text-[11px] text-slate-500">{u.created_at?.slice(0, 10)}</td>
                <td className="pr-4 text-right">
                  {u.user_id !== me?.user_id && (
                    <button data-testid={`revoke-user-${u.user_id}`} onClick={() => revoke(u)} className="text-slate-400 hover:text-red-500" style={{transition:"color 0.15s ease"}}><Trash size={13}/></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pending invites */}
      <div className="border border-slate-200 bg-white rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <EnvelopeSimple size={16} className="text-[#FFC107]" />
          <div className="overline">Pending Invites ({allowlist.length})</div>
        </div>
        {allowlist.length === 0 ? (
          <div className="grid-bg py-12 text-center text-sm text-slate-500">No pending invites. Click <span className="text-[#0F1E38] font-mono">Invite User</span> to add one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left overline border-b border-slate-100">
                <th className="px-5 py-2.5">Email</th><th>Role</th><th>Invited By</th><th>Invited</th><th></th>
              </tr>
            </thead>
            <tbody>
              {allowlist.map((a) => (
                <tr key={a.email} data-testid={`invite-row-${a.email}`} className="border-b border-slate-100">
                  <td className="px-5 py-3 font-mono text-[#0F1E38]">{a.email}</td>
                  <td><span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700">{a.role}</span></td>
                  <td className="text-xs text-slate-500 font-mono">{a.invited_by}</td>
                  <td className="text-xs text-slate-500 font-mono">{a.created_at?.slice(0, 10)}</td>
                  <td className="pr-4 text-right"><button data-testid={`revoke-invite-${a.email}`} onClick={() => removeInvite(a.email)} className="text-slate-400 hover:text-red-500" style={{transition:"color 0.15s ease"}}><Trash size={13}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
